import { useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createUserSchema,
  USER_ROLES,
  USER_ROLE_LABELS,
  type CreateUserInput,
  type UpdateUserInput,
  type User,
  type UserRole,
} from '@kn/shared';
import { api } from '../../api/client';
import { messageErreur } from '../../api/error-message';
import { useAuth } from '../../auth/auth-context';

const USERS_QUERY_KEY = ['users'] as const;

const FORMULAIRE_VIDE = {
  email: '',
  displayName: '',
  role: 'LECTURE' as UserRole,
  password: '',
};

export function UsersPage(): ReactElement {
  const queryClient = useQueryClient();
  const { user: utilisateurCourant } = useAuth();

  const [recherche, setRecherche] = useState('');
  const [formulaire, setFormulaire] = useState(FORMULAIRE_VIDE);
  const [formulaireOuvert, setFormulaireOuvert] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const listeQuery = useQuery({
    queryKey: [...USERS_QUERY_KEY, recherche],
    queryFn: () => api.listUsers({ search: recherche || undefined, pageSize: 200 }),
  });

  function rafraichir(texte: string): void {
    setErreur(null);
    setMessage(texte);
    void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
  }

  const creation = useMutation({
    mutationFn: (input: CreateUserInput) => api.createUser(input),
    onSuccess: (cree) => {
      setFormulaire(FORMULAIRE_VIDE);
      setFormulaireOuvert(false);
      rafraichir(`Compte créé : ${cree.email}`);
    },
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const modification = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateUserInput }) =>
      api.updateUser(id, patch),
    onSuccess: (modifie) => rafraichir(`Compte mis à jour : ${modifie.email}`),
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const suppression = useMutation({
    mutationFn: (id: string) => api.deleteUser(id),
    onSuccess: () => rafraichir('Compte supprimé.'),
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  function handleCreation(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setErreur(null);
    setMessage(null);

    const parsed = createUserSchema.safeParse(formulaire);
    if (!parsed.success) {
      setErreur(parsed.error.issues[0]?.message ?? 'Formulaire invalide.');
      return;
    }
    creation.mutate(parsed.data);
  }

  function handleSuppression(cible: User): void {
    setErreur(null);
    setMessage(null);
    const confirme = window.confirm(
      `Supprimer le compte ${cible.email} ?\n\n` +
        "La suppression n'est possible que pour un compte n'ayant jamais effectué " +
        "d'action tracée. Sinon, desactivez-le : l'historique doit rester rattachable " +
        'à son auteur.',
    );
    if (confirme) {
      suppression.mutate(cible.id);
    }
  }

  const enCours = creation.isPending || modification.isPending || suppression.isPending;

  return (
    <>
      <h1>Administration &mdash; Utilisateurs</h1>
      <p className="sous-titre">
        Création, modification et désactivation des comptes. Chaque action est enregistree dans la
        piste d&apos;audit. Un compte ayant déjà agi ne se supprimé pas : il se désactivé.
      </p>

      {erreur && (
        <p className="alerte alerte--erreur" role="alert">
          {erreur}
        </p>
      )}
      {message && <p className="alerte alerte--succes">{message}</p>}

      <div className="carte">
        <div className="barre-actions">
          <label className="champ" htmlFor="recherche">
            <span>Rechercher</span>
            <input
              id="recherche"
              type="search"
              placeholder="Nom ou adresse e-mail"
              value={recherche}
              onChange={(event) => setRecherche(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="bouton"
            onClick={() => setFormulaireOuvert((ouvert) => !ouvert)}
          >
            {formulaireOuvert ? 'Annuler' : 'Nouvel utilisateur'}
          </button>
        </div>

        {formulaireOuvert && (
          <form onSubmit={handleCreation} noValidate>
            <div className="barre-actions">
              <label className="champ" htmlFor="nouveau-email">
                <span>Adresse e-mail</span>
                <input
                  id="nouveau-email"
                  type="email"
                  value={formulaire.email}
                  onChange={(event) =>
                    setFormulaire((etat) => ({ ...etat, email: event.target.value }))
                  }
                />
              </label>
              <label className="champ" htmlFor="nouveau-nom">
                <span>Nom affiché</span>
                <input
                  id="nouveau-nom"
                  type="text"
                  value={formulaire.displayName}
                  onChange={(event) =>
                    setFormulaire((etat) => ({ ...etat, displayName: event.target.value }))
                  }
                />
              </label>
              <label className="champ" htmlFor="nouveau-role">
                <span>Rôle</span>
                <select
                  id="nouveau-role"
                  value={formulaire.role}
                  onChange={(event) =>
                    setFormulaire((etat) => ({ ...etat, role: event.target.value as UserRole }))
                  }
                >
                  {USER_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {USER_ROLE_LABELS[role]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="champ" htmlFor="nouveau-mdp">
                <span>Mot de passe initial</span>
                <input
                  id="nouveau-mdp"
                  type="text"
                  value={formulaire.password}
                  onChange={(event) =>
                    setFormulaire((etat) => ({ ...etat, password: event.target.value }))
                  }
                />
              </label>
              <button type="submit" className="bouton" disabled={enCours}>
                Créer le compte
              </button>
            </div>
            <p className="sous-titre">
              Le mot de passe doit contenir au moins 12 caractères, dont une minuscule, une
              majuscule et un chiffre. Il devra être change à la première connexion.
            </p>
          </form>
        )}
      </div>

      <div className="carte">
        {listeQuery.isPending && <p className="chargement">Chargement des comptes...</p>}
        {listeQuery.isError && (
          <p className="alerte alerte--erreur">{messageErreur(listeQuery.error)}</p>
        )}

        {listeQuery.data && (
          <table className="tableau">
            <caption className="sous-titre">
              {listeQuery.data.total} compte(s) enregistre(s)
            </caption>
            <thead>
              <tr>
                <th scope="col">Nom affiché</th>
                <th scope="col">Adresse e-mail</th>
                <th scope="col">Rôle</th>
                <th scope="col">État</th>
                <th scope="col">Créé le</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {listeQuery.data.items.map((compte) => {
                const estMoi = compte.id === utilisateurCourant?.id;
                return (
                  <tr key={compte.id}>
                    <td>
                      {compte.displayName}
                      {estMoi && ' (vous)'}
                    </td>
                    <td>{compte.email}</td>
                    <td>
                      <select
                        aria-label={`Rôle de ${compte.email}`}
                        value={compte.role}
                        disabled={estMoi || enCours}
                        onChange={(event) =>
                          modification.mutate({
                            id: compte.id,
                            patch: { role: event.target.value as UserRole },
                          })
                        }
                      >
                        {USER_ROLES.map((role) => (
                          <option key={role} value={role}>
                            {USER_ROLE_LABELS[role]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <span
                        className={`etiquette ${compte.isActive ? 'etiquette--actif' : 'etiquette--inactif'}`}
                      >
                        {compte.isActive ? 'Actif' : 'Désactivé'}
                      </span>
                    </td>
                    <td>{new Date(compte.createdAt).toLocaleDateString('fr-FR')}</td>
                    <td>
                      <button
                        type="button"
                        className="bouton bouton--lien"
                        disabled={estMoi || enCours}
                        onClick={() =>
                          modification.mutate({
                            id: compte.id,
                            patch: { isActive: !compte.isActive },
                          })
                        }
                      >
                        {compte.isActive ? 'Desactiver' : 'Réactiver'}
                      </button>
                      {' | '}
                      <button
                        type="button"
                        className="bouton bouton--lien"
                        disabled={estMoi || enCours}
                        title="Possible uniquement pour un compte sans aucune action tracée"
                        onClick={() => handleSuppression(compte)}
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
